import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link as MuiLink,
  Stack,
  TextField,
  Button,
  IconButton,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  function handleSubscribe(e) {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setSnack({ open: true, severity: 'error', message: 'Please enter a valid email.' });
      return;
    }
    setSnack({ open: true, severity: 'success', message: 'Subscribed — thanks! (demo)' });
    setEmail('');
  }

  // Grouped navigation for multi-column footer
  const navGroups = [
    {
      title: 'About',
      links: [
        { label: 'Home', path: '/' },
        { label: 'About', path: '/about' },
        { label: 'Careers', path: '/careers' },
        { label: 'Partners', path: '/partners' },
        { label: 'Contact', path: '/contact' }
      ]
    },
    {
      title: 'Services',
      links: [
        { label: 'Services', path: '/services' },
        { label: 'Portfolio', path: '/portfolio' },
        { label: 'Pricing', path: '/pricing' }
      ]
    },
    {
      title: 'Industries',
      links: [
        { label: 'Industries', path: '/industries' }
      ]
    },
    {
      title: 'Resources',
      links: [
        { label: 'Blog', path: '/blog' }
      ]
    }
  ];

  const aiTools = [
    { name: 'OpenAI', url: 'https://platform.openai.com/' },
    { name: 'Hugging Face', url: 'https://huggingface.co/' },
    { name: 'Cohere', url: 'https://cohere.ai/' }
  ];

  const samplePrompt = "Summarize this page for a CTO — include key benefits and a one-line CTA.";

  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(samplePrompt);
      setSnack({ open: true, severity: 'success', message: 'Sample prompt copied to clipboard' });
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: 'Unable to copy prompt' });
    }
  }

  return (
    <Box
      component="footer"
      sx={(t) => ({
        background: '#06292f',
        color: t.palette.getContrastText('#06292f'),
        borderTop: `1px solid ${t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : t.palette.divider}`,
        pt: { xs: 4, md: 6 },
        pb: { xs: 2, md: 3 },
        px: { xs: 0, md: 0 },
        fontSize: { xs: 15, md: 16 },
      })}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="flex-start">
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: 'white', letterSpacing: 0.5 }}>NehaInfoTech</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.92)', mb: 2, lineHeight: 1.7, fontSize: 16 }}>
                We build cloud-first solutions and digital experiences that help businesses grow.<br />Contact us for partnerships and career opportunities.
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <IconButton size="medium" color="inherit" component="a" href="https://github.com/karulmca" target="_blank" rel="noreferrer" aria-label="GitHub" sx={{ bgcolor: 'rgba(255,255,255,0.08)', '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' } }}>
                  <GitHubIcon fontSize="medium" />
                </IconButton>
                <IconButton size="medium" color="inherit" component="a" href="https://www.linkedin.com/company/nehainfotech" target="_blank" rel="noreferrer" aria-label="LinkedIn" sx={{ bgcolor: 'rgba(255,255,255,0.08)', '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' } }}>
                  <LinkedInIcon fontSize="medium" />
                </IconButton>
                <IconButton size="medium" color="inherit" component={RouterLink} to="/contact" aria-label="Contact" sx={{ bgcolor: 'rgba(255,255,255,0.08)', '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' } }}>
                  <MailOutlineIcon fontSize="medium" />
                </IconButton>
              </Stack>
            </Box>
          </Grid>
          <Grid item xs={12} sm={7} md={6}>
            <Grid container spacing={2}>
              {navGroups.map((group) => (
                <Grid item xs={6} sm={3} key={group.title}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'white', fontSize: 16, letterSpacing: 0.2 }}>{group.title}</Typography>
                  <Stack spacing={0.5}>
                    {group.links.map((p) => (
                      <MuiLink
                        key={p.path}
                        component={RouterLink}
                        to={p.path}
                        underline="none"
                        sx={{ color: 'rgba(255,255,255,0.93)', fontWeight: 500, fontSize: 15, py: 0.2, borderRadius: 1, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', textDecoration: 'underline' } }}
                      >
                        {p.label}
                      </MuiLink>
                    ))}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={6} sm={3} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'white', letterSpacing: 0.2 }}>AI Tools</Typography>
            <Stack spacing={1}>
              {aiTools.map((t) => (
                <MuiLink key={t.name} href={t.url} target="_blank" rel="noreferrer" underline="none" sx={{ color: 'rgba(255,255,255,0.97)', display: 'flex', alignItems: 'center', gap: 1.2, fontSize: 16, '&:hover': { color: '#fff', textDecoration: 'underline' } }}>
                  <OpenInNewIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />
                  <Typography variant="body2" sx={{ color: 'inherit', fontSize: 16 }}>{t.name}</Typography>
                </MuiLink>
              ))}
              <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button size="small" variant="outlined" onClick={copyPrompt} startIcon={<ContentCopyIcon />} sx={{ textTransform: 'none', borderColor: 'rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.95)', fontWeight: 600, fontSize: 15, px: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}>
                  Copy sample prompt
                </Button>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>Quick AI helper</Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
        <Divider sx={{ my: { xs: 3, md: 5 }, borderColor: 'rgba(255,255,255,0.18)' }} />
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, fontWeight: 500 }}>
              &copy; {new Date().getFullYear()} NehaInfoTech. All rights reserved.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, md: 0 } }}>
            <Stack direction="row" spacing={3} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <MuiLink component={RouterLink} to="/privacy" variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, fontWeight: 500, '&:hover': { color: '#fff', textDecoration: 'underline' } }}>
                Privacy
              </MuiLink>
              <MuiLink component={RouterLink} to="/terms" variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, fontWeight: 500, '&:hover': { color: '#fff', textDecoration: 'underline' } }}>
                Terms
              </MuiLink>
            </Stack>
          </Grid>
        </Grid>
      </Container>
      <Snackbar open={snack.open} autoHideDuration={6000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
